// Step 18 — Activity page.
//
// Composes what activity streams the backend already exposes for a
// workspace:
//   - Deal-situation snapshot log (Step 12, append-only)
//   - Recent notifications (Step 14)
//
// No new endpoint. Contract history + action history are per-object
// (getContractHistory, getDealActionHistory) so they'd bloat this
// page — those stay on the object detail views (existing panels).

import { useCallback, useEffect, useState } from "react";

import {
  listDealSituationSnapshots,
  listNotifications,
} from "../api";
import {
  AIEmpty,
  AIError,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";

function _fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE");
  } catch {
    return String(iso);
  }
}

export default function AIActivityPage() {
  const { loading: shellLoading, activeWorkspace, activeWorkspaceId } =
    useAIWorkspace();
  const [snapshots, setSnapshots] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;
      setLoading(true);
      setError(null);
      try {
        const [snap, notif] = await Promise.all([
          listDealSituationSnapshots(activeWorkspaceId, { limit: 20, signal }),
          listNotifications(activeWorkspaceId, { limit: 20, signal }),
        ]);
        setSnapshots(snap?.results || []);
        setNotifs(notif?.results || []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda aktivitet.");
      } finally {
        setLoading(false);
      }
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  if (shellLoading || (loading && snapshots.length === 0 && notifs.length === 0))
    return <AILoading />;
  if (!activeWorkspaceId) {
    return (
      <>
        <AIPageHeader title="Aktivitet" />
        <AIEmpty title="Ingen affär vald" />
      </>
    );
  }

  return (
    <>
      <AIPageHeader
        title="Aktivitet"
        subtitle={activeWorkspace?.organisation_name}
      />
      <AIError message={error} />

      <AISection title="Deal-situation-snapshots">
        {snapshots.length === 0 ? (
          <AIEmpty title="Inga snapshots ännu" />
        ) : (
          <ul className="qai-overview-list">
            {snapshots.map((s) => (
              <li key={s.id} className="qai-overview-list-item">
                <span className="qai-overview-list-label">
                  v{s.version} · {_fmtWhen(s.created_at)}
                </span>
                <span className="qai-overview-list-value">
                  {s.reason || "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </AISection>

      <AISection title="Notiser (senaste 20)">
        {notifs.length === 0 ? (
          <AIEmpty title="Inga notiser" />
        ) : (
          <ul className="qai-overview-list">
            {notifs.map((n) => (
              <li key={n.id} className="qai-overview-list-item">
                <span className="qai-overview-list-label">
                  {_fmtWhen(n.created_at)}
                </span>
                <span className="qai-overview-list-value">
                  {n.title || n.kind || "notis"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </AISection>
    </>
  );
}
